import { Link } from 'react-router-dom'
import { KPI_LOADING_DISPLAY } from '../../lib/managerial-kpis'
import type { CampaignPerformanceView } from '../../lib/campaign-performance'
import styles from './CampaignPerformancePanel.module.css'

interface CampaignPerformancePanelProps {
  rows: CampaignPerformanceView[]
  isLoading: boolean
  isBusy?: boolean
  hasData?: boolean
}

const STATUS_CLASS: Record<CampaignPerformanceView['status'], string> = {
  active: styles.statusActive,
  draft: styles.statusDraft,
  finished: styles.statusFinished,
  cancelled: styles.statusCancelled,
}

export function CampaignPerformancePanel({
  rows,
  isLoading,
  isBusy = false,
  hasData = true,
}: CampaignPerformancePanelProps) {
  const showEmptyRecorte = hasData && rows.length === 0

  return (
    <section
      className={styles.card}
      aria-labelledby="campaign-performance-title"
      aria-busy={isLoading || isBusy}
    >
      <div className={styles.header}>
        <h2 id="campaign-performance-title" className={styles.title}>
          Performance das campanhas
        </h2>
        <Link to="/campanhas" className={styles.seeAll}>
          Ver todas
        </Link>
      </div>

      <p className={styles.scopeNote}>
        Todas as campanhas com vendas reconhecidas, por GMV em todo o histórico,
        sem os filtros do topo ou corte mínimo de valor. Custos:
        WhatsApp, splits de terceiros e PIX. Receita da plataforma: parcela nos splits,
        antes das despesas operacionais. Take rate: receita da plataforma dividida pelo
        GMV.
      </p>

      {isLoading ? (
        <p className={styles.empty}>Carregando campanhas...</p>
      ) : showEmptyRecorte ? (
        <p className={styles.empty}>Nenhuma campanha com receita.</p>
      ) : rows.length === 0 ? (
        <p className={styles.empty}>{KPI_LOADING_DISPLAY}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Campanha</th>
                <th scope="col">Cliente</th>
                <th scope="col" className={styles.numeric}>
                  GMV
                </th>
                <th scope="col" className={styles.numeric}>
                  Custo
                </th>
                <th scope="col" className={styles.numeric}>
                  Receita da plataforma
                </th>
                <th scope="col" className={styles.numeric}>
                  Take rate
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={styles.campaign}>{row.campaignName}</td>
                  <td className={styles.client}>{row.clientName}</td>
                  <td className={`${styles.numeric} ${styles.amount}`}>
                    {row.formattedRevenue}
                  </td>
                  <td className={`${styles.numeric} ${styles.cost}`}>
                    {row.formattedCost}
                  </td>
                  <td className={`${styles.numeric} ${styles.amount}`}>
                    {row.formattedProfit}
                  </td>
                  <td className={`${styles.numeric} ${styles.amount}`}>
                    {row.formattedMargin}
                  </td>
                  <td>
                    <span className={`${styles.status} ${STATUS_CLASS[row.status]}`}>
                      <span className={styles.statusDot} aria-hidden="true" />
                      {row.statusLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
